import { useEffect } from 'react';
import { PUBLIC_URL } from '../utils/const';
import Layout from '../layouts/Layout';
import { PaddlePricing } from '../components/PaddlePricing';
import { useTranslation } from 'react-i18next';
import { TestimonialCard } from '../components/TestimonialCard';
import { BlogProperties, getBlogForLanguage } from 'reactyll';
import { blogs } from '../_blog/routes';
import { ExtraProperties } from '../components/BlogTemplate';
import { trackCtaClick } from '../utils/analytics';
import Seo from '../components/Seo';
import { homeJsonLd } from '../utils/seo';
import { useLocalizedPath } from '../utils/localeNav';
import { usePricing } from '../utils/usePricing';
import {
    Container,
    Grid,
    Title,
    Text,
    Button,
    Stack,
    Group,
    Flex,
    Accordion,
    Box,
    Anchor,
    Divider,
    Image,
    Paper,
    SimpleGrid,
    Table,
    ThemeIcon,
} from '@mantine/core';
import { IconCheck, IconExchange, IconLockFilled, IconSpy } from '@tabler/icons-react';
import { AppleSVG, GoogleSVG, ICSLogoSVG, OutlookSVG } from '../components/SVG';
import { redirectWithParameters } from '../utils/common';
import { HeroUseCases } from '../components/HeroUseCases';

// Shown until the localized Paddle price loads (and in the static HTML crawlers see).
const FALLBACK_MONTHLY_PRICE = '€3.50';
const FALLBACK_YEARLY_PRICE = '€29.00';

const CTAButtonProps = { color: 'primary.4', radius: 'md', fw: 400, c: 'white', px: 'xl', size: "compact-xl" as const }

const TrialButton = ({ onClick, mt }: { onClick: () => void, mt?: string }) => {
    const { t } = useTranslation();
    return (
        <Button
            {...CTAButtonProps}
            mt={mt}
            h="auto"
            py={8}
            onClick={onClick}
            styles={{ label: { flexDirection: 'column', gap: 1, whiteSpace: 'normal' } }}
        >
            <span>{t("home.hero.cta")}</span>
            <span style={{ fontSize: 10, lineHeight: 1.15, fontWeight: 400, color: 'white' }}>
                {t("home.hero.cta_trial")}
            </span>
        </Button>
    );
}

const CONTROL_ITEMS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;
const FAQ_ITEMS = ['1', '2', '3', '4', '5', '6', '7'] as const;
const BLOG_POSTS = ['synchronize-all-into-one', 'apple-calendar-synchronization', 'calensync-mcp'] as const;

const FaqElement = ({ question, answer }: { question: string, answer: string }) => {
    return (
        <div key={question}>
            <Accordion.Item value={question}>
                <Accordion.Control py='xs' styles={{ label: { fontSize: 'var(--mantine-font-size-lg)', fontWeight: '500' } }}><Title order={3} fw={600}>{question}</Title></Accordion.Control>
                <Accordion.Panel><Text fs='xl' dangerouslySetInnerHTML={{ __html: answer }} /></Accordion.Panel>
            </Accordion.Item>
            <Divider color='gray.5' />
        </div>
    )
}

const InnerHome: React.FC = () => {
    const { t, i18n } = useTranslation();
    const href = useLocalizedPath();
    const { monthly, yearly } = usePricing();
    const monthlyPrice = monthly?.formatted_subtotal ?? FALLBACK_MONTHLY_PRICE;
    const yearlyPrice = yearly?.formatted_subtotal ?? FALLBACK_YEARLY_PRICE;

    const signup = (cta_name: string) => {
        trackCtaClick('home', cta_name);
        redirectWithParameters(href('/login'), {}, true);
    }

    const scrollToHowItWorks = () => {
        trackCtaClick('home', 'how_it_works');
        document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    }

    useEffect(() => {
        // Handle initial hash navigation after component mount
        if (window.location.hash) {
            const id = window.location.hash.substring(1); // Remove the # character
            setTimeout(() => {
                const element = document.getElementById(id);
                element?.scrollIntoView({ behavior: 'smooth' });
            }, 100); // Small delay to ensure the element exists
        }
    }, []);

    const providers = [
        { name: 'Google Calendar', icon: <GoogleSVG width={28} height={28} /> },
        { name: 'Outlook & Microsoft 365', icon: <OutlookSVG width={28} height={28} /> },
        { name: 'Apple iCloud', icon: <AppleSVG width={28} height={28} /> },
        { name: t('home.hero.ics'), icon: <ICSLogoSVG width={28} height={28} /> },
    ];

    return (
        <>
            <Seo
                title={t('common.title_sync_calendars')}
                description={t('home.meta.description')}
                path="/"
                jsonLd={homeJsonLd(t('home.meta.description'))}
            />

            {/* Hero: the outcome and what it works with */}
            <Box className='hero'>
                <Container size="lg" pt={{ base: 'sm', md: 20 }} pb={{ base: 'xl', md: 56 }}>
                    <Title order={1} className='display-3' mb={{ base: 'xl', md: 48 }}>{t("home.hero.title")}</Title>
                    <Grid align="flex-start" gap={{ base: 40, md: 56 }}>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                            <Stack gap={36}>
                                <Text size="xl" fw={300} lh={1.6}>
                                    {t("home.hero.heading")}
                                    <br />
                                    <Anchor
                                        href="#pricing"
                                        c="primary.6"
                                        fw={500}
                                        underline="always"
                                        onClick={() => trackCtaClick('home', 'hero_pricing')}
                                    >
                                        {t("home.hero.price_link")}
                                    </Anchor>
                                    {' '}{t("home.hero.price_rest")}
                                </Text>
                                <Flex gap="md" justify={{ base: 'center', sm: 'flex-start' }} wrap="wrap" align="center">
                                    <TrialButton onClick={() => signup("hero")} />
                                    <Button variant="subtle" color="primary.6" size="compact-lg" onClick={scrollToHowItWorks}>
                                        {t("home.hero.secondary_cta")}
                                    </Button>
                                </Flex>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 7 }}>
                            <HeroUseCases />
                        </Grid.Col>
                    </Grid>
                    <Box mt={{ base: 48, md: 64 }}>
                        <Text size="xs" fw={600} c="primary.7" tt="uppercase" ta="center" mb="md" style={{ letterSpacing: '0.08em' }}>{t("home.hero.works_with")}</Text>
                        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
                            {providers.map((provider) => (
                                <Group key={provider.name} gap="sm" wrap="nowrap" justify="center" bg="rgba(255, 255, 255, 0.6)" px="md" py="sm" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                                    {provider.icon}
                                    <Text size="sm" fw={500}>{provider.name}</Text>
                                </Group>
                            ))}
                        </SimpleGrid>
                    </Box>
                </Container>
            </Box>

            {/* How it works */}
            <Box bg='gray.0' py="xl" id="how-it-works">
                <Container size="lg" my="xl">
                    <Title order={2} className='display-4' mb="xl">{t("home.how.title")}</Title>
                    <Grid gap="xl" align="center">
                        <Grid.Col span={{ base: 12, md: 5 }}>
                            <Stack gap="lg">
                                {(['1', '2', '3'] as const).map((step) => (
                                    <Stack key={step} gap={4}>
                                        <Text size="xs" fw={600} c="primary.6" tt="uppercase" style={{ letterSpacing: '0.06em' }}>{t("home.how.step", { number: step })}</Text>
                                        <Title order={3} fw={600}>{t(`home.how.${step}_title`)}</Title>
                                        <Text size="lg" fw={300}>{t(`home.how.${step}_body`)}</Text>
                                    </Stack>
                                ))}
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 7 }}>
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                aria-label={t("home.how.video_label")}
                                style={{
                                    isolation: 'isolate',
                                    width: '100%',
                                    height: 'auto',
                                    border: 'none',
                                    outline: 'none',
                                    borderRadius: '16px',
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                                }}
                                preload="none"
                            >
                                <source src={`${PUBLIC_URL}/calensync-hero.webm`} type="video/webm" />
                            </video>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            {/* Who it's for, with the matching customer quote */}
            <Container size="lg" my="xl" py="xl">
                <Title order={2} className='display-4' mb="xl">{t("home.audience.title")}</Title>
                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                    {([
                        { key: 'freelancers', testimonial: 4 },
                        { key: 'work_personal', testimonial: 5 },
                        { key: 'founders', testimonial: 0 },
                    ] as const).map(({ key, testimonial }) => (
                        <Paper key={key} withBorder radius="md" p="lg">
                            <Stack gap="sm" h="100%">
                                <Title order={3} fw={600}>{t(`home.audience.${key}_title`)}</Title>
                                <Text fw={300}>{t(`home.audience.${key}_body`)}</Text>
                                <Divider variant="dashed" mt="auto" />
                                <Text size="sm" c="dimmed" fs="italic" dangerouslySetInnerHTML={{ __html: t(`testimonials.${testimonial}.message`) }} />
                                <Text size="sm" fw={500}>{t(`testimonials.${testimonial}.author`)}, {t(`testimonials.${testimonial}.title`)}</Text>
                            </Stack>
                        </Paper>
                    ))}
                </SimpleGrid>
            </Container>

            {/* Control over what each calendar shows */}
            <Box bg='gray.0' py="xl" id="features">
                <Container size="lg" my="xl">
                    <Grid gap="xl" align="center">
                        <Grid.Col span={{ base: 12, md: 7 }}>
                            <Stack>
                                <Title order={2} className='display-4'>{t("home.control.title")}</Title>
                                <Text size="xl" fw={300} lh={1.5}>{t("home.control.intro")}</Text>
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mt="sm">
                                    {CONTROL_ITEMS.map((item) => (
                                        <Group key={item} gap="sm" wrap="nowrap" align="flex-start">
                                            <ThemeIcon size={22} radius="xl" color="primary.4" variant="light"><IconCheck size={14} stroke={3} /></ThemeIcon>
                                            <Text fw={300}>{t(`home.control.items.${item}`)}</Text>
                                        </Group>
                                    ))}
                                </SimpleGrid>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                            <Box mx="auto" maw="450px">
                                <Image loading='lazy' src={`${PUBLIC_URL}/assets/personal-to-professiona-busy.png`} alt={t("home.control.image_alt")} w="100%" />
                            </Box>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            {/* Pricing, then how it compares */}
            <Box className="hero" py="xl" id="pricing">
                <PaddlePricing
                    isHome={true}
                    size="lg"
                    onStartTrial={() => signup("pricing")}
                />
                <Container size="sm" mt="xl" id="compare">
                    <Stack gap="sm">
                        <Title order={2} className='display-6' ta="center">{t("home.compare.title")}</Title>
                        <Text ta="center" fw={300} size="lg">{t("home.compare.intro")}</Text>
                        <Paper radius="md" withBorder style={{ overflowX: 'auto' }}>
                            <Table verticalSpacing="sm" horizontalSpacing="md" miw={420}>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>{t("home.compare.col_product")}</Table.Th>
                                        <Table.Th ta="right">{t("home.compare.col_price")}</Table.Th>
                                        <Table.Th>{t("home.compare.col_limit")}</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    <Table.Tr bg="primary.0">
                                        <Table.Td fw={600}>Calensync</Table.Td>
                                        <Table.Td ta="right" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{yearlyPrice}</Table.Td>
                                        <Table.Td>{t("home.compare.unlimited")}</Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td>CalendarBridge Premium</Table.Td>
                                        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>$96</Table.Td>
                                        <Table.Td>{t("home.compare.limit_yearly", { count: 5 })}</Table.Td>
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td>OneCal Essential</Table.Td>
                                        <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>$120</Table.Td>
                                        <Table.Td>{t("home.compare.limit", { count: 5 })}</Table.Td>
                                    </Table.Tr>
                                </Table.Tbody>
                            </Table>
                        </Paper>
                        <Text size="xs" c="dimmed">{t("home.compare.note")}</Text>
                        <Title order={3} fw={600} mt="md">{t("home.compare.other_title")}</Title>
                        <Text fw={300}>{t("home.compare.other_body")}</Text>
                    </Stack>
                </Container>
            </Box>

            {/* Secondary features */}
            <Container size="lg" my="xl" py="xl">
                <Title order={2} className='display-4' mb="xl">{t("home.extras.title")}</Title>
                <Grid gap="xl" align="center">
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <Stack gap="xl">
                            <Stack gap={4}>
                                <Title order={3} fw={600}>{t("home.extras.scheduling_title")}</Title>
                                <Text size="lg" fw={300}>{t("home.extras.scheduling_body")}</Text>
                            </Stack>
                            <Stack gap={4}>
                                <Title order={3} fw={600}>{t("home.extras.ai_title")}</Title>
                                <Text size="lg" fw={300}>{t("home.extras.ai_body")}</Text>
                                <Anchor href={href('/mcp')} onClick={() => trackCtaClick('home', 'mcp')}>{t("home.extras.ai_link")}</Anchor>
                            </Stack>
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <Box mx="auto" maw="560px">
                            <Image loading="lazy" src={`${PUBLIC_URL}/assets/scheduling.png`} alt={t("home.extras.scheduling_alt")} w="100%" />
                        </Box>
                    </Grid.Col>
                </Grid>
            </Container>

            {/* Testimonials */}
            <Box bg='gray.0' py="xl">
                <Container size="lg" my="xl">
                    <Title order={2} className='display-4' mb="xl">{t("testimonials.title")}</Title>
                    <Grid>
                        <Grid.Col span={{ base: 12, lg: 6 }}>
                            {[1, 2, 3].map((idx) => (
                                <TestimonialCard key={idx} t={t} idx={idx} />
                            ))}
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 6 }}>
                            {[4, 5, 0].map((idx) => (
                                <TestimonialCard key={idx} t={t} idx={idx} />
                            ))}
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            {/* What happens to your data */}
            <Container size="lg" my="xl" py="xl">
                <Title order={2} className='display-4' mb="xl">{t("home.privacy.title")}</Title>
                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
                    {([
                        { key: 'oauth', icon: <IconExchange /> },
                        { key: 'encryption', icon: <IconLockFilled /> },
                        { key: 'reselling', icon: <IconSpy /> },
                    ] as const).map(({ key, icon }) => (
                        <Stack key={key} gap="xs">
                            <Group gap="xs">{icon}<Title order={3} fw={600}>{t(`home.privacy.${key}.title`)}</Title></Group>
                            <Text fw={300}>{t(`home.privacy.${key}.content`)}</Text>
                        </Stack>
                    ))}
                </SimpleGrid>
            </Container>

            {/* FAQ */}
            <Box bg="primary.0" py="xl">
                <Container size="lg" py="md">
                    <Title order={2} className='display-4' mb="xl">{t("home.questions.title")}</Title>
                    <Accordion variant="contained" color='primary.4'>
                        {FAQ_ITEMS.map((idx) => (
                            <FaqElement key={idx} question={t(`home.questions.q${idx}`)} answer={t(`home.questions.a${idx}`)} />
                        ))}
                        <FaqElement key="outlook-guide" question={t(`home.faq.q13`)} answer={t(`home.faq.a13`, { post: `${PUBLIC_URL}${getBlogForLanguage(blogs['sync-outlook-to-google'], i18n.language, "en").url}` })} />
                    </Accordion>
                </Container>
            </Box>

            {/* Final CTA */}
            <Container size="md" py="xl" my="xl">
                <Stack align="center" gap="sm">
                    <Title order={2} className='display-4' ta="center">{t("home.final.title")}</Title>
                    <Text size="lg" fw={300} ta="center">{t("home.final.body")}</Text>
                    <TrialButton mt="sm" onClick={() => signup("final")} />
                    <Text size="sm" ta="center">{t("home.final.micro", { monthly: monthlyPrice })}</Text>
                </Stack>
            </Container>

            {/* A few posts, linked rather than featured */}
            <Box bg='gray.0' py="lg">
                <Container size="lg">
                    <Group gap="lg" justify="center">
                        <Text fw={500}>{t("home.blog.title")}</Text>
                        {BLOG_POSTS.map((slug) => {
                            const blog = getBlogForLanguage(blogs[slug], i18n.language, "en") as BlogProperties & ExtraProperties;
                            return <Anchor key={slug} href={`${blog.url}`}>{blog.title}</Anchor>;
                        })}
                        <Anchor href={`${PUBLIC_URL}/blog`}>{t("home.blog.more")}</Anchor>
                    </Group>
                </Container>
            </Box>
        </>
    );
};

const Home: React.FC = () => {
    return (
        <Layout onlyRequired={false}>
            <InnerHome />
        </Layout>
    );
}

export default Home;
